import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "src/design";
import { colorTextMuted, radiusInput } from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}

export const AddCategoryModal = ({ open, onClose, onSubmit }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
              {t("menu.modal.category.title")}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: colorTextMuted, mt: 0.25 }}>
              {t("menu.modal.category.subtitle")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
          {t("menu.modal.category.name")}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("menu.modal.category.placeholder")}
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: radiusInput } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <CustomButton color="inverted" onClick={onClose}>
          {t("common.cancel")}
        </CustomButton>
        <CustomButton color="black" onClick={handleSubmit} disabled={!name.trim()} disabledColor="rgba(255,255,255,0.4)">
          {t("menu.modal.category.addButton")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
